import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { orderKeys } from '@/features/orders/hooks/queryKeys';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type {
  CreatePurgeRuleInput,
  PurgeInput,
  PurgePreview,
  PurgeRule,
  PurgeRun,
  UpdatePurgeRuleInput,
} from '@restaurant/api-contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const PREVIEW_PATH = '/api/restaurants/{restaurantId}/purge/preview';
const EXECUTE_PATH = '/api/restaurants/{restaurantId}/purge/execute';
const RUNS_PATH = '/api/restaurants/{restaurantId}/purge/runs';
const RULES_PATH = '/api/restaurants/{restaurantId}/purge/rules';
const RULE_PATH = '/api/restaurants/{restaurantId}/purge/rules/{ruleId}';

export const purgeKeys = {
  rules: (restaurantId: string) => ['purge', 'rules', restaurantId] as const,
  runs: (restaurantId: string) => ['purge', 'runs', restaurantId] as const,
  preview: (restaurantId: string, input: PurgeInput | null) =>
    ['purge', 'preview', restaurantId, input] as const,
};

export function usePurgeRules() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: purgeKeys.rules(restaurantId),
    queryFn: async () =>
      unwrapResult<PurgeRule[]>(
        await openapiClient.GET(RULES_PATH, { params: { path: { restaurantId } } }),
      ),
    enabled: !!restaurantId,
  });
}

export function usePurgeRuns(limit = 50) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: purgeKeys.runs(restaurantId),
    queryFn: async () =>
      unwrapResult<PurgeRun[]>(
        await openapiClient.GET(RUNS_PATH, {
          params: { path: { restaurantId }, query: { limit } },
        }),
      ),
    enabled: !!restaurantId,
  });
}

export function usePurgePreview(input: PurgeInput | null, limit = 20) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: purgeKeys.preview(restaurantId, input),
    queryFn: async () => {
      if (!input) return null;
      return unwrapResult<PurgePreview>(
        await openapiClient.POST(PREVIEW_PATH, {
          params: { path: { restaurantId } },
          body: { input, limit },
        }),
      );
    },
    enabled: !!restaurantId && input !== null,
  });
}

function usePurgeInvalidation() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: purgeKeys.rules(restaurantId) });
    void queryClient.invalidateQueries({ queryKey: purgeKeys.runs(restaurantId) });
    void queryClient.invalidateQueries({ queryKey: ['purge', 'preview', restaurantId] });
    void queryClient.invalidateQueries({ queryKey: orderKeys.all(restaurantId) });
    void queryClient.invalidateQueries({ queryKey: ['reservations'] });
  };
}

export function useCreatePurgeRule() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const invalidate = usePurgeInvalidation();
  return useMutation({
    mutationFn: async (body: CreatePurgeRuleInput) =>
      unwrapResult<PurgeRule>(
        await openapiClient.POST(RULES_PATH, { params: { path: { restaurantId } }, body }),
      ),
    onSuccess: invalidate,
  });
}

export function useUpdatePurgeRule() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const invalidate = usePurgeInvalidation();
  return useMutation({
    mutationFn: async ({ ruleId, data }: { ruleId: string; data: UpdatePurgeRuleInput }) =>
      unwrapResult<PurgeRule>(
        await openapiClient.PATCH(RULE_PATH, {
          params: { path: { restaurantId, ruleId } },
          body: data,
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useRunPurge() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const invalidate = usePurgeInvalidation();
  return useMutation({
    mutationFn: async ({
      input,
      ruleId,
      expectedCount,
    }: {
      input: PurgeInput;
      ruleId?: string;
      expectedCount: number;
    }) =>
      unwrapResult<PurgeRun>(
        await openapiClient.POST(EXECUTE_PATH, {
          params: { path: { restaurantId } },
          body: { input, ruleId, expectedCount },
        }),
      ),
    onSettled: invalidate,
  });
}
